import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import CalendarLink from '@/components/ui/calendar-link';
import { CheckCircle } from 'lucide-react';
import Link from 'next/link';

export function PricingSection() {
	return (
		<section className="py-20 bg-white">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-16">
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						Simple, Transparent Pricing
					</h2>
					<p className="text-xl text-gray-600 max-w-3xl mx-auto">
						No hidden fees. No setup costs. Just results that scale with your business.
					</p>
				</div>

				<div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
					{/* 1K Plan */}
					<Card className="border-gray-200">
						<CardHeader className="text-center">
							<CardTitle className="text-lg">Starter</CardTitle>
							<div className="text-2xl font-bold text-gray-900 mt-4">
								$10<span className="text-base text-gray-600">/mo</span>
							</div>
							<p className="text-gray-600">Perfect for getting started</p>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>1,000 emails/month</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Unlimited mailboxes</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>AI-powered routing</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Real-time analytics</span>
							</div>
							<div className="pt-4">
								<Link
									href="/signup"
									className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 font-medium transition-colors text-center block"
								>
									Get Started
								</Link>
							</div>
						</CardContent>
					</Card>

					{/* 10K Plan */}
					<Card className="border-emerald-200 ring-2 ring-emerald-500 relative">
						<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
							<Badge className="bg-emerald-500 text-white">Most Popular</Badge>
						</div>
						<CardHeader className="text-center">
							<CardTitle className="text-lg">Growth</CardTitle>
							<div className="text-2xl font-bold text-gray-900 mt-4">
								$100<span className="text-base text-gray-600">/mo</span>
							</div>
							<p className="text-gray-600">For growing businesses</p>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>10,000 emails/month</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Unlimited mailboxes</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>AI-powered routing</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Priority support</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Advanced analytics</span>
							</div>
							<div className="pt-4">
								<Link
									href="/signup"
									className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 font-medium transition-colors text-center block"
								>
									Get Started
								</Link>
							</div>
						</CardContent>
					</Card>

					{/* 50K Plan */}
					<Card className="border-gray-200">
						<CardHeader className="text-center">
							<CardTitle className="text-lg">Scale</CardTitle>
							<div className="text-2xl font-bold text-gray-900 mt-4">
								$500<span className="text-base text-gray-600">/mo</span>
							</div>
							<p className="text-gray-600">For scaling teams</p>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>50,000 emails/month</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Unlimited mailboxes</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>AI-powered routing</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Priority support</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Advanced analytics</span>
							</div>
							<div className="pt-4">
								<Link
									href="/signup"
									className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 font-medium transition-colors text-center block"
								>
									Get Started
								</Link>
							</div>
						</CardContent>
					</Card>

					{/* 100K Plan */}
					<Card className="border-gray-200">
						<CardHeader className="text-center">
							<CardTitle className="text-lg">Enterprise</CardTitle>
							<div className="text-2xl font-bold text-gray-900 mt-4">
								$1000<span className="text-base text-gray-600">/mo</span>
							</div>
							<p className="text-gray-600">For large organizations</p>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>100,000 emails/month</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Unlimited mailboxes</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>AI-powered routing</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Dedicated support</span>
							</div>
							<div className="flex items-center space-x-2">
								<CheckCircle className="h-4 w-4 text-emerald-600" />
								<span>Custom integrations</span>
							</div>
							<div className="pt-4">
								<CalendarLink
									target="_blank"
									href="https://cal.com/team/nosagent/founders"
									className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-md px-4 py-2 font-medium transition-colors text-center block"
								>
									Contact Sales
								</CalendarLink>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</section>
	);
}
